<template>
  <div class="site-footer critical-component-clear">
    <div class="container">
      <!-- desktop nav -->
      <nav
        v-if="!screen.mobile"
        class="site-footer__navigation-desktop"
      >
        <template
          v-for="menu in navItems"
        >
          <site-footer-nav
            v-if="menu.content"
            :key="menu.title"
            :title="menu.title"
            :menu="menu.content"
            :block-class="'site-footer'"
          />
        </template>
      </nav>

      <!-- social -->
      <div class="site-footer__social">
        <div class="site-footer__image-wrapper">
          <div class="site-footer__image-inner">
            <img
              class="site-footer__image"
              :src="logo.src"
              :alt="logo.alt"
            >
          </div>
        </div>

        <ul class="site-footer__social-list">
          <template
            v-for="(url, social) in socials"
            :key="social"
          >
            <li
              v-if="url"
              class="site-footer__social-item"
            >
              <a
                :href="url"
                :aria-label="social"
                target="_blank"
              >
                <component :is="`icon-${social}`" />
              </a>
            </li>
          </template>
        </ul>
      </div>

      <!-- mobile nav accordion -->
      <div
        v-if="screen.mobile"
        class="site-footer__navigation site-footer__accordion"
      >
        <accordion-item
          v-for="(menu, index) in navItems"
          :key="`${menu.title}-${index}`"
          :index="index"
          :content="menu.content"
          :toggle-index="toggleIndex"
          :multi-open="true"
          :block-class="'site-footer'"
          @accordionToggle="handleAccordionToggle"
        >
          <template #title>
            {{ menu.title }}
          </template>
          <template #content>
            <site-footer-nav
              v-if="menu.content"
              :menu="menu.content"
              :block-class="'site-footer'"
            />
          </template>
        </accordion-item>
      </div>

      <!-- legal -->
      <div class="site-footer__legal">
        <div class="site-footer__legal-group">
          <span
            class="site-footer text-body-s"
            v-text="copyRight"
          />
        </div>

        <div class="site-footer__legal-group">
          <ul class="site-footer__legal-list">
            <li
              class="site-footer__legal-item"
            >
              <a
                class="text-body-s"
                :href="legal.privacyPolicy.url"
                v-text="legal.privacyPolicy.text"
              />
            </li>


            <li
              v-if="legal.oneTrust"
              class="site-footer__legal-item"
            >
              <!-- OneTrust Cookies Settings button start -->
              <button id="ot-sdk-btn" class="text-body-s ot-sdk-show-settings"
                v-text="$string('general.site_footer.one_trust.cookie_settings')"
              >

              </button>
              <!-- OneTrust Cookies Settings button end -->
            </li>

          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Site footer
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 */
import { mapState } from 'vuex'
import SiteFooterNav from '~async/site-footer/site-footer-nav'
import AccordionItem from '~async/accordion/accordion-item'
import IconFacebook from '~icons/social/facebook.svg'
import IconInstagram from '~icons/social/instagram.svg'
import IconPinterest from '~icons/social/pinterest.svg'
import IconTwitter from '~icons/social/twitter.svg'
import IconYoutube from '~icons/social/youtube.svg'

export default {
  name: 'SiteFooter',

  components: {
    SiteFooterNav,
    AccordionItem,
    IconFacebook,
    IconInstagram,
    IconPinterest,
    IconTwitter,
    IconYoutube,
  },

  props: {
    shopName: {
      type: String,
      default: '',
    },

    primary: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },

    secondary: {
      type: Object,
      default: () => ({
        handle: '',
        items: [],
      }),
    },

    legal: {
      type: Object,
      default: () => ({
        handle: '',
        currentYear: '',
        privacyPolicy: {},
        oneTrust: '',
      }),
    },

    logo: {
      type: Object,
      default: () => ({
        src: '',
        alt: '',
      }),
    },

    socials: {
      type: Object,
      default: () => ({
        facebook: '',
        instagram: '',
        twitter: '',
        youtube: '',
        pinterest: '',
      }),
    },

  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      screen: (state) => state.index.screen,
    }),

    copyRight() {
      const copyRightYear = this.$string('general.site_footer.copyright_note')
        .replace('{{ current_year }}', this.legal.currentYear)
      return `${copyRightYear} ${this.shopName}`
    },

    navItems() {
      const items = [
        {
          title: this.$string('general.site_footer.primary_menu_title'),
          content: this.primary,
        },
        {
          title: this.$string('general.site_footer.secondary_menu_title'),
          content: this.secondary,
        },
      ]

      return items
    },
  },

  data() {
    return {
      toggleIndex: {},
      mobile: null,
    }
  },

  mounted() {
    this.setResponsiveNav()

    if (this.legal.oneTrust) {
      this.appendCookieLawScript()
    }
  },

  methods: {

    /**
     * Append One Trust Script so that the footer opt out button is recognized upon mount
     */
    appendCookieLawScript() {
      const cookieLawScript = document.createElement('script')
      cookieLawScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js'
      cookieLawScript.type = 'text/javascript'
      cookieLawScript.setAttribute('data-language', 'en')
      cookieLawScript.setAttribute('charset', 'UTF-8')
      cookieLawScript.setAttribute('data-domain-script', '41b75559-86fa-4864-8d15-6cd73efb8b72')
      document.head.appendChild(cookieLawScript)
    },

    /**
     * Handles accordion item click
     * @param {Number} index - index of accordion that was clicked
     * - force updates the toggle index
     */
    handleAccordionToggle(index) {
      this.toggleIndex = {
        index,
      }
    },

    /**
     * Set the navigation version for mobile/desktop devices.
     */
    setResponsiveNav() {
      if (this.screen.mobile) {
        this.mobile = false
        return
      }

      this.mobile = true
    },
  },
}
</script>

<style lang="scss">
@import './site-footer';
</style>
